import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ConfigProvider, App, theme as antTheme } from 'antd';
import {
  ThemeMode,
  DEFAULT_THEME,
  isDarkTheme,
  applyTheme,
  getStoredTheme,
  persistTheme,
} from './theme';
import { readCssVar } from './css-vars';
import { useAuthStore } from '../core/store/auth.store';

type ThemeContextValue = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const buildAntTheme = (isDark: boolean) => ({
  algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
  token: {
    colorPrimary: readCssVar('primary', isDark ? '#60a5fa' : '#1d4ed8'),
    borderRadius: 8,
    colorBgContainer: readCssVar('surface', isDark ? '#202a38' : '#ffffff'),
    colorBgLayout: readCssVar('bgStart', isDark ? '#111827' : '#f5f7fa'),
    colorText: readCssVar('text', isDark ? '#f1f5f9' : '#172033'),
    colorTextSecondary: readCssVar('textMuted', isDark ? '#cbd5e1' : '#5b6878'),
    colorBorder: readCssVar('border', isDark ? '#334155' : '#dce2e8'),
    colorSuccess: readCssVar('success', isDark ? '#86efac' : '#15803d'),
    colorWarning: readCssVar('warning', isDark ? '#f4c76f' : '#b45309'),
    colorError: readCssVar('danger', isDark ? '#f87171' : '#b91c1c'),
  },
  components: {
    Table: {
      headerBg: readCssVar('hoverBg', isDark ? '#273344' : '#eef2f7'),
      headerColor: readCssVar('text', isDark ? '#f1f5f9' : '#172033'),
      borderColor: readCssVar('border', isDark ? '#334155' : '#dce2e8'),
    },
    Tabs: {
      itemColor: readCssVar('textMuted', isDark ? '#cbd5e1' : '#5b6878'),
      itemSelectedColor: readCssVar('primary', isDark ? '#60a5fa' : '#1d4ed8'),
      inkBarColor: readCssVar('primary', isDark ? '#60a5fa' : '#1d4ed8'),
    },
    Menu: {
      itemSelectedBg: readCssVar('accentSoft', isDark ? '#1e3a5f' : '#e8eefc'),
      itemSelectedColor: readCssVar('primary', isDark ? '#60a5fa' : '#1d4ed8'),
      itemHoverBg: readCssVar('surfaceHover', isDark ? '#29384a' : '#edf2f7'),
      itemHoverColor: readCssVar(
        'primaryHover',
        isDark ? '#93c5fd' : '#1e40af'
      ),
    },
  },
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const themeUserId = useAuthStore(
    (state) =>
      state.auth?.payload?.sub ||
      state.auth?.payload?.preferred_username ||
      null
  );
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    getStoredTheme(themeUserId)
  );
  const [antdThemeConfig, setAntdThemeConfig] = useState(() =>
    buildAntTheme(isDarkTheme(theme))
  );

  const setTheme = useCallback(
    (nextTheme: ThemeMode) => {
      setThemeState(nextTheme);
      persistTheme(nextTheme, themeUserId);
    },
    [themeUserId]
  );

  const toggleTheme = useCallback(() => {
    setTheme(isDarkTheme(theme) ? DEFAULT_THEME : 'dark');
  }, [setTheme, theme]);

  useEffect(() => {
    applyTheme(theme);
    setAntdThemeConfig(buildAntTheme(isDarkTheme(theme)));
  }, [theme]);

  useEffect(() => {
    setThemeState(getStoredTheme(themeUserId));
  }, [themeUserId]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark: isDarkTheme(theme),
    }),
    [setTheme, theme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdThemeConfig}>
        <App>{children}</App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};
